import React from 'react';

/**
 * App-wide React Error Boundary.
 *
 * Wraps every routed page so that if a rendering exception bubbles up
 * (which normally produces a fully blank white screen and no console
 * feedback the user can see), we render a helpful diagnostic card
 * instead — showing the error, the current URL, and buttons to reload
 * or return to the dashboard.
 *
 * This is critical for the recurring "production shows blank screen"
 * reports where the underlying cause was a runtime crash in a page
 * component after a partial deploy or a data shape mismatch.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[ErrorBoundary]', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoDashboard = () => {
    window.location.assign('/dashboard');
  };

  render() {
    if (!this.state.error) return this.props.children;

    const err = this.state.error;
    return (
      <div
        className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
        data-testid="app-error-boundary"
      >
        <div className="max-w-2xl w-full bg-white border border-red-200 rounded-xl shadow-lg overflow-hidden">
          <div className="bg-red-50 px-6 py-4 border-b border-red-200">
            <h2 className="text-xl font-bold text-red-800 flex items-center gap-2">
              <span aria-hidden="true">⚠️</span> Something broke on this page
            </h2>
            <p className="text-sm text-red-700 mt-1">
              The application encountered an unexpected error while rendering this view.
              Your data is safe — no changes were lost.
            </p>
          </div>
          <div className="px-6 py-5 space-y-4">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Current URL
              </div>
              <div className="font-mono text-sm text-slate-800 break-all bg-slate-100 rounded px-2 py-1 mt-1">
                {typeof window !== 'undefined' ? window.location.href : ''}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Error
              </div>
              <div className="font-mono text-xs text-red-800 whitespace-pre-wrap bg-red-50 rounded px-2 py-2 mt-1">
                {err?.name || 'Error'}: {err?.message || String(err)}
              </div>
            </div>
            {this.state.errorInfo?.componentStack && (
              <details className="text-xs">
                <summary className="cursor-pointer text-slate-600 hover:text-slate-800">
                  Component stack (for developers)
                </summary>
                <pre className="font-mono text-[11px] text-slate-600 whitespace-pre-wrap bg-slate-100 rounded p-2 mt-1 max-h-64 overflow-auto">
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700"
                data-testid="error-boundary-reload"
              >
                Reload page
              </button>
              <button
                type="button"
                onClick={this.handleGoDashboard}
                className="px-4 py-2 rounded-md border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-100"
                data-testid="error-boundary-go-dashboard"
              >
                Back to Dashboard
              </button>
            </div>
            <p className="text-xs text-slate-500 pt-2 border-t">
              If this keeps happening, please share the error above with support.
              Common causes: a recent deploy left the frontend and backend out of
              sync (hard-refresh with Cmd/Ctrl+Shift+R), or the backend returned
              data in an unexpected shape.
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
